import React from "react";

import styles from "./TableSelect.module.css";

import ITableSelectProps from "./dto/ITableSelectProps";
import TableSelectState from "./dto/TableSelectState";
import { ColorClassNames, FontClassNames, IColumn, Label, SelectionMode, DetailsRowCheck, IDetailsRowProps } from "@fluentui/react";
import TableSelectConfig from "../../../../dto/Form/TableSelectConfig";
import StickyHeaderDetailsList from "../StickyHeaderDetailsList/StickyHeaderDetailsList";
import TableSelectRow from "../../../../dto/Form/TableSelectRow";
import fixEnum from "../../../../util/FixEnum";
import TableSelectColumnType from "../../../../dto/Form/TableSelectColumnType";

export default class TableSelect extends React.Component<ITableSelectProps, TableSelectState> {
    private _lastSelectedRow: string = "";
    private _blockSelectionCallbackId: string = "";

    constructor(props: ITableSelectProps) {
        super(props);
        let state = new TableSelectState();
        state.columns = this.buildColumns(props);
        state.rows = this.buildRows(props);
        state.selectedRows = state.rows.map(r => r.id);
        this.state = state;
    }

    public render() {
        return (
            <div>
                {this.props.field.title && <Label required={this.props.field.required}>{this.props.field.title}</Label>}
                {this.props.errorMessage && <div className={`${ColorClassNames.red} ${FontClassNames.small} ${styles.errorMessage}`}>{this.props.errorMessage}</div>}
                <StickyHeaderDetailsList items={this.state.rows} columns={this.state.columns} selectionMode={SelectionMode.none} getKey={(item: TableSelectRow) => item.id} onRenderRow={this.onRenderRow} overwrittenRowHeight={62}
                    onActiveItemChanged={(item: TableSelectRow) => this.onRowSelectionChanged(null, item)}></StickyHeaderDetailsList>
            </div>
        );
    }

    private onRenderRow = (
        props?: IDetailsRowProps,
        defaultRender?: (props?: IDetailsRowProps) => JSX.Element | null
      ): JSX.Element | null => {
        if (!defaultRender) {
          return null;
        }
    
        if (
          props &&
          props.item &&
          this.state.selectedRows.indexOf(props.item.id) >= 0
        ) {
          props.className = props.className
            ? `${props.className} is-selected`
            : "is-selected";
        }
    
        return defaultRender(props);
      };

    private onRenderSelectionColumn = (item: TableSelectRow): JSX.Element => {
        let selected = this.state.selectedRows.indexOf(item.id) >= 0;

        return (
            <div
                key={`selection_${item.id}`}
                className={styles.tableSelectionCell}
                onClick={(ev) => {
                    this.onRowSelectionChanged(ev, item);
                }}
            >
                <DetailsRowCheck
                    canSelect={true}
                    selected={selected}
                    compact
                ></DetailsRowCheck>
            </div>
        );
    };

    private onRenderSelectAllHeader = (): JSX.Element => {
        let selected =
            this.state.selectedRows.length === this.state.rows.length &&
            this.state.rows.length > 0;

        return (
            <div
                key={`selectall`}
                onClick={this.onToggleSelectAll}
                onMouseEnter={() => this.setHoveringSelectAll(true)}
                onMouseLeave={() => this.setHoveringSelectAll(false)}
            >
                <DetailsRowCheck
                    canSelect={true}
                    selected={selected}
                    isHeader={true}
                    isVisible={
                        selected || this.state.hoveringSelectAll
                    }
                    compact
                ></DetailsRowCheck>
            </div>
        );
    };

    private onToggleSelectAll = (): void => {
        let selectedRows: string[] = [];
        if (this.state.selectedRows.length !== this.state.rows.length) {
            selectedRows = this.state.rows.map((item) => item.id);
        } else {
            selectedRows = [];
        }

        this.setState(
            {
                selectedRows: selectedRows,
            },
            () => {
                this.serializeItems();
            }
        );
    };

    private setHoveringSelectAll = (hovering: boolean): void => {
        this.setState({
            hoveringSelectAll: hovering,
        });
    };

    private buildColumns(props: ITableSelectProps): IColumn[] {
        let config = props.field.config as TableSelectConfig;
        let columns: IColumn[] = [
            {
                key: "selection",
                name: "",
                minWidth: 25,
                maxWidth: 50,
                onRender: this.onRenderSelectionColumn,
                onRenderHeader: this.onRenderSelectAllHeader,
                className: styles.tableSelectionColumn,
            }
        ];
        for (let curItem of config.columns) {
            let column: IColumn = {
                key: curItem.internalName,
                name: curItem.label,
                fieldName: curItem.internalName,
                minWidth: 100,
                maxWidth: 500,
                onRender: (item: TableSelectRow, _index?: number, column?: IColumn) => {
                    let contextText = item.values[column?.key as string] ? item.values[column?.key as string] : "";
                    let content: JSX.Element | string;
                    if(column && column.data === TableSelectColumnType.link) {
                        content = <a href={contextText} target="_blank" rel="noopener noreferrer">{contextText}</a>;
                    } else {
                        content = contextText;
                    }

                    return (
                        <div className={styles.tableRowContainer}>
                            <span className={styles.tableRowText}>{content}</span>
                        </div>
                    )
                },
                isResizable: true,
                data: fixEnum(curItem.columnType, TableSelectColumnType)
            };
            columns.push(column);
        }
        return columns;
    }

    private buildRows(props: ITableSelectProps): TableSelectRow[] {
        let config = props.field.config as TableSelectConfig;
        return config.rows;
    }

    private onRowSelectionChanged = (
        ev: React.MouseEvent | null,
        item: TableSelectRow
    ): void => {
        if(ev) {
            ev.stopPropagation();
        }

        if(item.id === this._blockSelectionCallbackId) {
            return;
        }

        this._blockSelectionCallbackId = item.id;
        setTimeout(() => {
            this._blockSelectionCallbackId = "";
        }, 150);

        let selectedRows = [...this.state.selectedRows];
        let selectedIndex = selectedRows.indexOf(item.id);
        if (selectedIndex >= 0) {
            selectedRows.splice(selectedIndex, 1);
            this._lastSelectedRow = "";
        } else {
            selectedRows.push(item.id);

            if (ev && ev.shiftKey && this._lastSelectedRow) {
                let lastSelectedRowIndex = this.state.rows.findIndex(
                    (i) => i.id === this._lastSelectedRow
                );
                let currentIndex = this.state.rows.findIndex((i) => i.id === item.id);
                if (lastSelectedRowIndex >= 0 && currentIndex >= 0) {
                    let start = Math.min(lastSelectedRowIndex, currentIndex);
                    let end = Math.max(lastSelectedRowIndex, currentIndex);

                    selectedRows = [];
                    for (let curRow = start; curRow <= end; ++curRow) {
                        selectedRows.push(this.state.rows[curRow].id);
                    }
                }
            } else {
                this._lastSelectedRow = item.id;
            }
        }

        this.setState(
            {
                selectedRows: selectedRows,
            },
            () => {
                this.serializeItems();
            }
        );
    };

    private serializeItems = (): void => {
        this.setState({
            rows: [...this.state.rows],
        }, () => {
            this.props.onChange(JSON.stringify(this.state.selectedRows));
        })
    };
}